/**
 * UTM Parameter Tracking Utility
 * Captures UTM parameters on page load, persists across session,
 * and provides them for form submissions.
 * 
 * UTM parameters are NEVER exposed to users - only stored internally.
 */

const UTM_STORAGE_KEY = 'cafa_utm_params';
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

/**
 * Capture UTM parameters from the current URL and store them
 * Called once on app initialization
 */
export function captureUTMParams() {
  const urlParams = new URLSearchParams(window.location.search);
  const utmData = {};
  let hasUTM = false;

  UTM_PARAMS.forEach(param => {
    const value = urlParams.get(param);
    if (value) {
      utmData[param] = value;
      hasUTM = true;
    }
  });

  // Only store if we have UTM params and don't already have stored ones
  // (preserve the original entry point UTMs)
  if (hasUTM && !sessionStorage.getItem(UTM_STORAGE_KEY)) {
    utmData.captured_at = new Date().toISOString();
    utmData.landing_page = window.location.pathname;
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmData));
    
    // Clean UTMs from URL without triggering navigation (cosmetic cleanup)
    // This prevents UTMs from being visible in the browser bar
    if (window.history.replaceState) {
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }
}

/**
 * Get stored UTM parameters for form submission
 * Returns null if no UTMs were captured
 */
export function getStoredUTMParams() {
  const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Clear stored UTM parameters (e.g., after successful conversion)
 */
export function clearUTMParams() {
  sessionStorage.removeItem(UTM_STORAGE_KEY);
}

/**
 * Check if current session has UTM data
 */
export function hasUTMParams() {
  return sessionStorage.getItem(UTM_STORAGE_KEY) !== null;
}

/**
 * Get attribution source - combines UTM data with self-reported source
 * Useful for analytics
 */
export function getAttributionData(selfReportedSource) {
  const utm = getStoredUTMParams();
  return {
    self_reported: selfReportedSource,
    utm_data: utm,
    has_utm: utm !== null
  };
}
